import store from "@/store";
import axios from "axios";
import { getOrigin, getPathAPI, getAuthHeaders, extractErrorMessage } from ".";

export function makeBlobUrlFromBase64(base64String: string): string {
  const byteCharacters = atob(base64String);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: "application/pdf" });
  const blobUrl = URL.createObjectURL(blob);
  return blobUrl;
}

type ErrorMode =
  | { kind: "throw" }
  | { kind: "swallow" }
  | { kind: "notify"; vm: Vue };

async function printDocumentToTab(opts: {
  url: string;
  data: Record<string, any>;
  storeModule: string;
  loadingPath?: string;
  errorMode: ErrorMode;
}): Promise<void> {
  const { url, data, storeModule, loadingPath = "noMessage", errorMode } = opts;
  const newTab = window.open(
    `${getOrigin()}/loading-preview/${loadingPath}`,
    "_blank"
  );
  store.commit(`${storeModule}/SET_STATES`, { makingApiRequest: true });

  try {
    const response = await axios({
      url: `${getPathAPI()}${url}`,
      method: "POST",
      data,
      headers: getAuthHeaders()
    });
    const blobUrl = makeBlobUrlFromBase64(response.data.response.document);
    if (newTab) {
      newTab.location.href = blobUrl;
      // Revoke once the tab has loaded the PDF; fall back to a generous
      // timeout if the load event never fires (cross-origin, closed tab, etc).
      const revoke = () => URL.revokeObjectURL(blobUrl);
      newTab.addEventListener?.("load", revoke, { once: true });
      window.setTimeout(revoke, 60000);
    } else {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    newTab?.close();
    if (errorMode.kind === "throw") {
      throw new Error(extractErrorMessage(error));
    }
    if (errorMode.kind === "notify") {
      errorMode.vm.$appNotifyError((error as any)?.message);
    }
    // swallow: do nothing
  } finally {
    store.commit(`${storeModule}/SET_STATES`, { makingApiRequest: false });
  }
}

export async function downloadLossFormDocument(
  this: Vue,
  data: Record<string, any>
): Promise<void> {
  return printDocumentToTab({
    url: "/storage-forms/print-claims-lookup-by-claim-number-form",
    data,
    storeModule: "claims",
    errorMode: { kind: "notify", vm: this }
  });
}

export async function downloadClaimsDocumentPolicyNumber(
  data: Record<string, any>
): Promise<void> {
  return printDocumentToTab({
    url: "/storage-forms/print-claims-lookup-by-policy-number-form",
    data,
    storeModule: "claims",
    errorMode: { kind: "swallow" }
  });
}

export async function downloadClaimsDocument(
  data: Record<string, any>
): Promise<void> {
  return printDocumentToTab({
    url: "/storage-forms/print-claims-storage-form",
    data,
    storeModule: "claims",
    errorMode: { kind: "throw" }
  });
}

export async function downloadQuoteDocument(
  data: Record<string, any>,
  loadingString: any = "noMessage"
): Promise<void> {
  return printDocumentToTab({
    url: "/quotes/print",
    data,
    storeModule: "quote",
    loadingPath: loadingString,
    errorMode: { kind: "throw" }
  });
}

export async function downloadPersonalLiabilityDocument(
  data: Record<string, any>
): Promise<void> {
  return printDocumentToTab({
    url: "/personal-liability-policies/print",
    data,
    storeModule: "personalLiabilityPolicy",
    errorMode: { kind: "throw" }
  });
}

export async function printSingleChangeRequest(
  this: Vue,
  printData: Record<string, any>
) {
  const newTab = window.open(
    `${getOrigin()}/loading-preview/noMessage`,
    "_blank"
  );
  try {
    const data = {
      _ids: Array.isArray(printData.changeRequestIds)
        ? printData.changeRequestIds
        : [printData.changeRequestIds],
      site: "ATLAS",
      policyPrefix: printData.policyPrefix,
      printMethod: printData.printMethod || "old"
    };
    const fileURL = await store.dispatch(`${printData.printAction}`, data);

    if (fileURL) {
      const url = makeBlobUrlFromBase64(fileURL.document);
      if (newTab) {
        newTab.location.href = url;
        const revoke = () => URL.revokeObjectURL(url);
        newTab.addEventListener?.("load", revoke, { once: true });
        window.setTimeout(revoke, 60000);
      } else {
        URL.revokeObjectURL(url);
      }
      this.$appNotifySuccess("Printing change request");
    } else {
      newTab?.close();
      this.$appNotifyError("Error printing request. Try again later.");
    }
  } catch (error) {
    newTab?.close();
    this.$appNotifyError((error as any)?.message);
  }
}
